import { useSelector } from 'react-redux';
import { useLocale } from '../../../contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import type { RootState } from '@/presentation/store';
import { Navigator } from '@/presentation/routing/Navigator';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { ThemeSelectorSheet } from '@/presentation/components/common/sheet/themeSelector/ThemeSelectorSheet';
import { LanguageSelectorSheet } from '@/presentation/components/common/sheet/languageSelector/LanguageSelectorSheet';
import { ListItemTile } from '@/presentation/components/common/ListItemTile';
import { MenuItemType } from '@/domain/enums/menuItem/menuItemType';

export const HomeTopBarBody = () => {
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserId = currentUser?.firebaseId;

  const notImplementedYet = () => {
    InfoSheet.showNotImplementedYet({ l10n });
  };

  type MenuItem = {
    type: MenuItemType;
    label: string;
    iconPath: string;
    action: () => void;
  };

  const menuItems: MenuItem[] = [
    {
      type: MenuItemType.PREMIUM_PACKAGES,
      label: l10n.topBar.premiumPackages,
      iconPath: '/assets/images/premium.png',
      action: () => Navigator.navigateToPremiumPackages(),
    },
    {
      type: MenuItemType.CAMPAIGNS,
      label: l10n.topBar.campaigns,
      iconPath: '/assets/images/campaign.png',
      action: () => Navigator.navigateToCampaigns(),
    },
    {
      type: MenuItemType.IDEAS,
      label: l10n.topBar.ideas,
      iconPath: '/assets/images/roadmap.png',
      action: () => Navigator.navigateToRoadmap(),
    },
    {
      type: MenuItemType.FEEDBACK,
      label: l10n.helpAbout.feedback,
      iconPath: '/assets/images/feedback.png',
      action: () => Navigator.navigateToFeedback(),
    },
    {
      type: MenuItemType.THEME,
      label: l10n.settings.theme,
      iconPath: '/assets/vectors/theme.svg',
      action: () => ThemeSelectorSheet.show({}),
    },
    {
      type: MenuItemType.LANGUAGE,
      label: l10n.settings.language,
      iconPath: '/assets/vectors/language.svg',
      action: () =>
        LanguageSelectorSheet.show({
          fixedHeight: responsive.languageSelectorSheet.fixedHeight,
        }),
    },
    {
      type: MenuItemType.SHARE,
      label: l10n.helpAbout.share,
      iconPath: '/assets/images/share_social.png',
      action: notImplementedYet,
    },
  ];

  // Profile is gated on a signed-in user (the gate moved here from the header):
  // only prepend the row when there is a currentUserId so it never navigates nowhere.
  if (currentUserId) {
    menuItems.unshift({
      type: MenuItemType.PROFILE,
      label: l10n.topBar.profile,
      iconPath: '/assets/images/avatar.png',
      action: () => Navigator.navigateToUserDetails(currentUserId),
    });
  }

  return (
    <div style={{ padding: responsive.spacing.base }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: responsive.spacing.sm }}>
        {menuItems.map((item) => (
          <ListItemTile
            key={item.type}
            iconPath={item.iconPath}
            title={item.label}
            onClick={item.action}
            qa={{ id: `topbar-menu-${item.type}` }}
          />
        ))}
      </div>
    </div>
  );
};
